<script lang="ts" setup>
import { trackEvent } from '@application-insights/aiUtils';
import {
  type VragenSbiCodesSbiBeschrijvingDto,
  type VragenSbiCodesSbiCodeDto,
} from '@services/vragen-public/api';
import {
  sbiCodesGetSbiCodes,
  useSbiCodesGetSbiCodes,
} from '@services/vragen-public/orval-api/sbi-codes/sbi-codes';
import { ModalType } from '@shared/component/types';
import VModal from '@shared/component/VModal/VModal.vue';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useId } from '@tools/composables/id';
import { capitalize } from '@tools/helpers';
import { isEnterKey } from '@tools/keyboard';
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  type UnwrapRef,
  useAttrs,
  watch,
} from 'vue';
import { type UpdateEventOf } from '../types';
import SbiOpzoekOption from './SbiOpzoekOption.vue';
import type { SbiCodeEvent } from './types';

export interface SbiOpzoekProps {
  hasError?: boolean;
  path?: string;
  label?: string;
  value?: string | SbiCodeEvent;
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  searchThrottle?: number;
  lookupDetails: (value: string) => Promise<VragenSbiCodesSbiBeschrijvingDto | undefined>;
  clearable?: boolean;
  id?: string;
}

const props = withDefaults(defineProps<SbiOpzoekProps>(), {
  label: 'SBI zoeken',
  placeholder: 'maak een keuze...',
  disabled: false,
  readonly: false,
  searchThrottle: 4,
  clearable: true,
  id: undefined,
});

const emit = defineEmits<{
  updateField: [value: UpdateEventOf<SbiCodeEvent | undefined>];
  'update:value': [value?: SbiCodeEvent];
}>();

const state = reactive({
  searchTerm: '',
});

const optionsId = useId('options');

const { data: sbiCodes, isLoading } = useSbiCodesGetSbiCodes(
  computed(() => ({
    search: state.searchTerm,
  })),
  {
    query: {
      enabled: () => state.searchTerm.length >= props.searchThrottle,
      select: ({ result }) => result,
    },
  },
);

const formattedValue = computed(() => {
  let valueToFormat: string | undefined;

  // gaat nu om zowel string als SbiCodeEvent object
  if (typeof props.value === 'string') {
    valueToFormat = props.value;
  } else if (props.value && typeof props.value === 'object' && 'sbicode' in props.value) {
    valueToFormat = props.value.sbicode;
  } else {
    valueToFormat = undefined;
  }

  if (!valueToFormat || valueToFormat?.includes('.')) {
    return valueToFormat;
  }

  const arrayValue = Array.from(valueToFormat);

  if (arrayValue.length > 2) {
    arrayValue.splice(2, 0, '.');

    if (arrayValue.length > 5) {
      arrayValue.splice(5, 0, '.');
    }
  }

  return arrayValue.join('');
});

const attrs = useAttrs();
const root = ref();
const vsearch = ref();
const val = ref<string | undefined>(formattedValue.value);
const details = ref<VragenSbiCodesSbiBeschrijvingDto | null>();
const searchTerm = ref<string>();
const clearSearch = ref(false);
const focusSearch = ref(false);
const isOpen = ref(false);
const currentSelectIndex = ref<number>(0);
const showDetails = ref(false);

const flatMapResults = computed(
  () =>
    sbiCodes.value?.reduce(
      (a, b) => a.concat(getChildrenOrSelf(b)),
      [] as VragenSbiCodesSbiCodeDto[],
    ) ?? [],
);

function onShowDetails() {
  showDetails.value = true;
  trackEvent('track-sbi-details-geopend', { sbi: val.value });
}

function onDown() {
  if (currentSelectIndex.value >= flatMapResults.value.length - 1) return;

  currentSelectIndex.value = (currentSelectIndex.value ?? 0) + 1;

  const el = document.querySelector(`#option-${currentSelectIndex.value}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
}

function onUp() {
  currentSelectIndex.value = Math.max((currentSelectIndex.value ?? 0) - 1, 0);

  const el = document.querySelector(`#option-${currentSelectIndex.value}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
}

function onEnter() {
  const selected = flatMapResults.value[currentSelectIndex.value];
  if (!selected) return;
  onSelect({ code: selected.code, omschrijving: selected.omschrijving, children: [] });
}

function fetchKeyboardEvent(event: KeyboardEvent) {
  if (isEnterKey(event)) stopPropagation(event);

  if (event.type === 'keydown' && event.code === 'ArrowDown') stopPropagation(event);
  if (event.type === 'keydown' && event.code === 'ArrowUp') stopPropagation(event);

  if (event.type === 'keyup' && event.code === 'Tab') isOpen.value = true;
  if (event.type === 'keyup' && event.code === 'ArrowDown') onDown();
  if (event.type === 'keyup' && event.code === 'ArrowUp') onUp();
  if (event.type === 'keyup' && isEnterKey(event)) onEnter();
}

function getChildrenOrSelf(sbiDto: VragenSbiCodesSbiCodeDto): VragenSbiCodesSbiCodeDto[] {
  if (sbiDto.children === undefined || sbiDto.children === null) return [];
  const children =
    sbiDto.children.reduce(
      (a, b) => a.concat(b.children ?? [b]),
      [] as VragenSbiCodesSbiCodeDto[],
    ) ?? [];
  if (children.length === 0) return [sbiDto];
  return children;
}

// make sure the page does not scroll through pressing key up/down space bar when the list is open
function stopPropagation(event: Event) {
  event.preventDefault();
  event.stopPropagation();
}

watch(
  () => isOpen.value,
  (value) => {
    focusSearch.value = value;
  },
);

function toggleList() {
  isOpen.value = !isOpen.value;
  clearSearch.value = !clearSearch.value;

  // de wens om de search term te wissen bij openen van de lijst
  if (isOpen.value) {
    state.searchTerm = '';
  }
}

function handleClickOutside(evt: MouseEvent) {
  evt.stopPropagation();

  if (!root.value.contains(evt.target)) {
    isOpen.value = false;
    return;
  }
  if (vsearch.value?.contains(evt.target)) {
    isOpen.value = true;
  }
}

function onSelect(event: VragenSbiCodesSbiCodeDto) {
  const rawSbiCode = event.code.replaceAll('.', '');

  val.value = `${event.code} - ${capitalize(event.omschrijving)}`;
  emit('updateField', {
    path: props.path,
    value: { rawSbiCode, sbicode: event.code, input: searchTerm.value },
  });
  emit('update:value', { rawSbiCode, sbicode: event.code, input: searchTerm.value });
  getDetails(event.code);
  isOpen.value = false;
}

async function getDetails(code: string) {
  details.value = await props.lookupDetails(code);
}

function clear() {
  val.value = undefined;
  emit('updateField', { path: props.path, value: undefined });
  emit('update:value', undefined);
}

async function initValueDetails() {
  if (!formattedValue.value) {
    val.value = undefined;
    return;
  }
  const res = await sbiCodesGetSbiCodes({ search: formattedValue.value });
  const deepest = getDeepestChild(res.result);
  if (deepest) {
    val.value = `${deepest[0].code} - ${capitalize(deepest[0].omschrijving)}`;
    await getDetails(deepest[0].code);
  }
}

onMounted(async () => {
  if (!props.readonly) document.addEventListener('click', handleClickOutside);
  await initValueDetails();
});

function getDeepestChild(
  _sbiCodes: VragenSbiCodesSbiCodeDto[] | undefined,
): VragenSbiCodesSbiCodeDto[] | undefined {
  if (!_sbiCodes || !_sbiCodes.length) return undefined;
  if (!_sbiCodes[0].children) return _sbiCodes;
  return getDeepestChild(_sbiCodes[0].children);
}

function onUpdateVal(newValue?: string) {
  if (newValue) {
    return;
  }

  details.value = null;
}

function updateCurrentSelectIndex(newFlatMapResults: UnwrapRef<typeof flatMapResults>) {
  currentSelectIndex.value = newFlatMapResults.findIndex(
    ({ code }) => code === formattedValue.value,
  );
}

watch(flatMapResults, updateCurrentSelectIndex);
watch(() => props.value, initValueDetails);
watch(val, onUpdateVal);

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="flex flex-grow flex-col flex-nowrap" :id="props.id" v-bind="attrs">
    <div
      v-if="!readonly"
      class="styled-input select cursor-pointer after:pointer-events-none after:absolute after:right-[1.125em] after:top-1/2 after:z-10 after:m-0 after:block after:aspect-square after:origin-center after:-translate-y-3/4 after:-rotate-45 after:rounded-sm after:border-[3px] after:border-r-0 after:border-t-0 after:border-solid em:after:w-2.5 motion-safe:transition motion-safe:after:transition-colors"
      :class="{
        disabled,
        filled: val,
        'cursor-not-allowed after:border-grey-600': props.disabled,
        'border-b-red bg-red/10 after:border-red': props.hasError,
        'after:z-0 after:border-primary hover:bg-primary-200 focus:bg-primary-200':
          !props.hasError && !props.disabled,
      }"
      ref="root"
      @click="toggleList"
      tabindex="0"
      data-testid="sbiopzoek-select"
      @keyup="fetchKeyboardEvent"
      @keydown="fetchKeyboardEvent"
    >
      <div
        :aria-controls="optionsId"
        :aria-expanded="isOpen && !disabled"
        class="line-clamp-1 max-w-[calc(100%_-_2rem)] overflow-hidden text-ellipsis"
        data-testid="sbiopzoek-selection"
        role="combobox"
      >
        <template v-if="val">
          {{ val }}
        </template>

        <span v-else class="text-grey-400">
          {{ placeholder || '&nbsp;' }}
        </span>
      </div>
      <button
        v-if="val && clearable && !disabled"
        aria-label="clear"
        class="absolute right-10 z-[2] text-grey-300"
        @click.stop="clear"
        data-testid="sbiopzoek-clear"
        type="button"
      >
        <FontAwesomeIcon icon="fa-solid fa-times" />
      </button>
      <div
        v-if="isOpen && !disabled"
        class="absolute right-0 top-full z-overlay w-full flex-col bg-white p-5 shadow"
        :class="{
          flex: isOpen,
          hidden: !isOpen,
        }"
        :id="optionsId"
      >
        <div ref="vsearch" class="em:mb-4" data-testid="search-box" @click.stop>
          <VSearch
            v-model="state.searchTerm"
            :aria-label="props.label"
            placeholder="type hier minimaal 4 tekens"
            :clear="clearSearch"
            :on-focus="focusSearch"
            @onTab="toggleList"
          />
        </div>
        <div v-if="isLoading">
          <VSkeleton height="lg" :count="6" />
        </div>
        <div v-else class="flex max-h-72 flex-col overflow-y-auto overflow-x-hidden">
          <SbiOpzoekOption
            :select-index="currentSelectIndex"
            :options="sbiCodes"
            @onSbiSelected="onSelect"
            :value="val"
          />
          <span v-if="!sbiCodes?.length">Geen resultaten gevonden</span>
        </div>
      </div>
    </div>
    <div v-else class="filled group-[]/float-label:h-11 group-[]/float-label:content-center">
      {{ val || '-' }}
    </div>
    <span v-if="details" class="mt-1 flex items-center" :class="{ 'ml-3': !readonly }">
      <a
        class="cursor-pointer text-primary hover:text-primary-600"
        @click.prevent="onShowDetails()"
      >
        Toelichting
      </a>
      <FontAwesomeIcon icon="fa-regular fa-eye" class="ml-1 text-primary" />
    </span>
  </div>
  <VModal
    v-if="showDetails"
    :title="details?.title"
    :type="ModalType.Success"
    :loading="isLoading"
    confirm-button-text=""
    cancel-button-text="Sluiten"
    @close-modal="showDetails = false"
    is-active
    ><small v-html="details?.description" class="whitespace-pre-wrap"></small>
  </VModal>
</template>
